<template>

    <appStartModal
        ref="appStartModel"
        :appName="selectedAppName"
        @job_started="jobStartedHandler"
    />

  <div v-if="loading" class="apps-list__loading">
    <div class="lds-ring"><div /><div /><div /><div /></div>
  </div>
  <div v-else-if="apps && apps.length > 0" class="apps-list">
        <AppCard v-for="app in apps" :key="app.name" 
            :params="app"
            @app_selected="jobSelectedHandler"
        />
    </div>
  <div v-else-if="apps !== null" class="apps-list__empty">
    <p>Filter search is empty</p>
  </div>


</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, watch, nextTick } from 'vue';

import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import AppCard from '@/components/Apps/AppCard.vue';
import appStartModal from '@/components/Apps/AppStartModal.vue';
import { listFrontendApps } from '@/assets/js/apps/apps-manager';

import { useStore } from 'vuex';
const store = useStore();

const $toast = useToast();
const emit = defineEmits(['job_started']);
const props = defineProps({
  filterType: { type: String, default: null },
  searchName: { type: String, default: '' },
  searchTitle: { type: String, default: '' },
  searchAuthor: { type: String, default: '' },
});
const appStartModel = ref(null)
const selectedAppName = ref('')
const loading = ref(false);
const apps = ref(null);

const dataConnect = new DatastoreConnect();

async function loadApps() {
  loading.value = true;
  apps.value = null;
  const filter = {};
  if (props.filterType) filter.type = props.filterType;
  if (props.searchName) filter.name = props.searchName;
  if (props.searchTitle) filter.title = props.searchTitle;
  if (props.searchAuthor) filter.author = props.searchAuthor;
  const params = Object.keys(filter).length > 0 ? { filter } : {};
  const frontendApps = listFrontendApps(filter);
  // Merge both frontend and backend apps in the list of apps.
  dataConnect.api('app.list', params)
  .then((ret) => {
    if (ret.result) {
      const backendApps = Array.isArray(ret.result) ? ret.result : [];
      const mergedApps = [...backendApps];
      const seenNames = new Set(backendApps.map((app) => app.name));

      for (const frontendApp of frontendApps) {
        if (!seenNames.has(frontendApp.name)) {
          mergedApps.push(frontendApp);
        }
      }

      apps.value = mergedApps;
    } else {
      apps.value = frontendApps;
    }

    if (ret.error) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: ret.error.message,
        type: 'error',
        position: 'top',
      });
    }
  })
   .catch((error) => {
      console.log(error);
      apps.value = frontendApps;
      return '';
    })
   .finally(() => {
     loading.value = false;
   });
}

onMounted(async () => {
  await loadApps();
});

watch(() => props.filterType, () => { loadApps(); });
watch(() => props.searchName, () => { loadApps(); });
watch(() => props.searchTitle, () => { loadApps(); });
watch(() => props.searchAuthor, () => { loadApps(); });

function jobStartedHandler(params) {
 emit('job_started');
}

async function jobSelectedHandler(name) {
  selectedAppName.value = name;
  await nextTick();
  appStartModel.value?.showModal();
}

</script>

<style lang="scss" scoped>

.apps-list {
    padding: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: start;
    gap: 16px;
}

.apps-list__loading {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
}

.apps-list__empty {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
  font-size: 0.9rem;
  font-style: italic;
  color: #888;
}
</style>
